import fetcher from 'api/fetcher';
import { getValidateCampaignFields, getValidateErrors } from 'utils/campaign/validation';
import { getActiveCropTypes, sortProfRoles } from 'utils/clickme';
import { isFirstStep, isSecondStep, isThirdStep } from 'utils/constants/campaign';
import { MagritteNotificationName } from 'utils/constants/notification';

import campaignCreateStep1NextFormSubmit from '@hh.ru/analytics-js-events/build/clickme/employer/common/campaign_create_step1_next_form_submit';
import campaignCreateStep2NextFormSubmit from '@hh.ru/analytics-js-events/build/clickme/employer/common/campaign_create_step2_next_form_submit';
import campaignCreateStep3StartFormSubmit from '@hh.ru/analytics-js-events/build/clickme/employer/common/campaign_create_step3_start_form_submit';

import { saveCrops } from 'components/common/BannerImg/CropImageModal/saveCrops';
import { saveCampaignAndBanner } from 'components/CpcFlexibleCreate/CpcActions/saveCampaignAndBanner';
import { loadSourceFormConfig } from 'components/CpcFlexibleCreate/CpcForm/AdSources/loadSourceFormConfig';

import { ProfRoles, setProfRoles } from 'store/autoReducers';
import { BannerTextGenerationState } from 'store/models/bannerTextGeneration';
import { BannerErrors, CreateBannerState, setBannerErrors } from 'store/models/createBanner';
import {
    CampaignErrors,
    CreateCampaignState,
    setCampaignErrors,
    setCampaignFormStep,
} from 'store/models/createCampaign';
import { addMagritteNotification } from 'store/models/notification';
import { AppThunkAction } from 'store/utils';

type StateForValidate = CreateCampaignState &
    CreateBannerState &
    BannerTextGenerationState & {
        profRoles: ProfRoles[];
    };

type ErrorHandlerCommon = (
    stateForValidate: StateForValidate,
    currentStep: number,
    errors: Record<string, string>
) => AppThunkAction<{
    errorKeys: string[];
    stateErrors: Partial<CampaignErrors & BannerErrors> | undefined;
}>;

type SendInternalAnalytics = (
    state: StateForValidate,
    currentStep: number,
    errorsObject: Record<string, string>
) => void;

type FirstStepFetcher = (currentStep: number, stateForValidate: StateForValidate) => AppThunkAction;

type SecondStepFetcher = (currentStep: number, stateForValidate: StateForValidate) => AppThunkAction;

type ThirdStepFetcher = (
    currentStep: number,
    stateForValidate: StateForValidate,
    bannerTextGenerationState: BannerTextGenerationState,
    setSaving: (isSaving: boolean) => void,
    dashboardRoute: string
) => AppThunkAction;

type SetNextStepForCampaignAndBanner = (
    currentStep: number,
    setSaving: (isSaving: boolean) => void,
    dashboardRoute: string
) => AppThunkAction;

const sendInternalAnalytics: SendInternalAnalytics = (state, currentStep, errorsObject) => {
    const {
        bannerVacancyId,
        bannerTitle: title,
        bannerText: text,
        bannerLink: url,
        adAgeTo: ageMax,
        adAgeFrom: ageMin,
        adLanguage,
        adProfessions,
        areas,
        adGender: sex,
        adSalary,
        adSource,
        adUnknownAge: unknownAge,
        adTotalBudget: budget,
        adDailyBudget: budgetDaily,
        bannerTextGenerationCount,
        bannerTextType,
    } = state;

    const vacancyId = Number(bannerVacancyId);
    const languages = JSON.stringify(adLanguage);
    const professions = JSON.stringify(adProfessions);
    const regions = JSON.stringify(areas);
    const sources = JSON.stringify(adSource);
    const salary = JSON.stringify(adSalary);
    const errors = Object.keys(errorsObject).length !== 0 ? JSON.stringify(errorsObject) : null;

    if (isFirstStep(currentStep)) {
        campaignCreateStep1NextFormSubmit({
            vacancyId,
            title,
            text,
            url,
            errors,
        });
    }
    if (isSecondStep(currentStep)) {
        campaignCreateStep2NextFormSubmit({
            vacancyId,
            ageMax,
            ageMin,
            languages,
            professions,
            regions,
            sex,
            sources,
            unknownAge,
            errors,
        });
    }
    if (isThirdStep(currentStep)) {
        campaignCreateStep3StartFormSubmit({
            vacancyId,
            sex,
            salary,
            regions,
            age: `${ageMin}, ${ageMax}`,
            languages,
            sources,
            budget,
            budgetDaily,
            budgetSources: '',
            manualBudgets: false,
            errors,
            triesCount: bannerTextGenerationCount,
            bannerTextType,
        });
    }
};

const errorHandlerCommon: ErrorHandlerCommon = (stateForValidate, currentStep, errors) => {
    return (dispatch) => {
        const errorKeys = Object.keys(errors);
        sendInternalAnalytics(stateForValidate, currentStep, errors);
        let stateErrors;

        if (errorKeys.length > 0) {
            stateErrors = getValidateErrors(errors);
            dispatch(addMagritteNotification(MagritteNotificationName.CampaignFormFieldsError));
        }
        return { errorKeys, stateErrors };
    };
};

const firstStepFetcher: FirstStepFetcher = (currentStep, stateForValidate) => {
    return async (dispatch) => {
        const fields = getValidateCampaignFields(currentStep, stateForValidate);

        try {
            const requests = [
                fetcher.post<unknown, Record<string, string>>(`/api/v1/validator/campaign`, fields),
                fetcher.get<unknown, { professionalCategories: ProfRoles[] }>('/api/v1/catalog/professionalCatalog'),
            ];

            dispatch(setBannerErrors({}));

            const [errors, prof] = await Promise.all(requests);

            const { errorKeys, stateErrors } = dispatch(
                errorHandlerCommon(stateForValidate, currentStep, errors as Record<string, string>)
            );

            const hasError = errorKeys.length > 0;

            if (hasError) {
                dispatch(setBannerErrors(stateErrors as Partial<BannerErrors>));
            } else {
                const actions = [setCampaignFormStep(currentStep + 1), setCampaignErrors({}), setBannerErrors({})];

                dispatch(setProfRoles(sortProfRoles(prof.professionalCategories as ProfRoles[])));
                dispatch(actions);
            }
        } catch (e) {
            dispatch(addMagritteNotification(MagritteNotificationName.SomethingWentWrong));
        }
    };
};

const secondStepFetcher: SecondStepFetcher = (currentStep, stateForValidate) => {
    return async (dispatch) => {
        const fields = getValidateCampaignFields(currentStep, stateForValidate);

        try {
            const errors = await fetcher.post<unknown, Record<string, string>>(`/api/v1/validator/campaign`, fields);
            const { errorKeys, stateErrors } = dispatch(errorHandlerCommon(stateForValidate, currentStep, errors));
            const hasError = errorKeys.length > 0;

            if (hasError) {
                dispatch(setCampaignErrors(stateErrors as Partial<CampaignErrors>));
            } else {
                dispatch([setCampaignFormStep(currentStep + 1), setCampaignErrors({}), setBannerErrors({})]);
                await dispatch(loadSourceFormConfig());
            }
        } catch (e) {
            dispatch(addMagritteNotification(MagritteNotificationName.SomethingWentWrong));
        }
    };
};

const thirdStepFetcher: ThirdStepFetcher = (
    currentStep,
    stateForValidate,
    bannerTextGenerationState,
    setSaving,
    dashboardRoute
) => {
    return async (dispatch) => {
        const fields = getValidateCampaignFields(currentStep, stateForValidate);

        try {
            const activeCropTypes = getActiveCropTypes(stateForValidate.adSource);
            setSaving(true);
            await dispatch(saveCrops(activeCropTypes));

            const errors = await fetcher.post<unknown, Record<string, string>>(`/api/v1/validator/campaign`, fields);

            const { errorKeys, stateErrors } = dispatch(errorHandlerCommon(stateForValidate, currentStep, errors));
            const hasError = errorKeys.length > 0;

            if (hasError) {
                dispatch(setCampaignErrors(stateErrors as Partial<CampaignErrors>));
            } else {
                await dispatch(saveCampaignAndBanner(setSaving, dashboardRoute));
            }
        } catch (errors) {
            dispatch(addMagritteNotification(MagritteNotificationName.CreateCampaignSomethingWentWrongError));
        } finally {
            setSaving(false);
        }
    };
};

export const setNextStepForCampaignAndBanner: SetNextStepForCampaignAndBanner = (
    currentStep,
    setSaving,
    dashboardRoute
) => {
    return async (dispatch, getState) => {
        const { createBanner, createCampaign, profRoles, bannerTextGeneration } = getState();
        const stateForValidate = { ...createBanner, ...createCampaign, ...bannerTextGeneration, profRoles };

        if (isFirstStep(currentStep)) {
            await dispatch(firstStepFetcher(currentStep, stateForValidate));
        }

        if (isSecondStep(currentStep)) {
            await dispatch(secondStepFetcher(currentStep, stateForValidate));
        }

        if (isThirdStep(currentStep)) {
            await dispatch(
                thirdStepFetcher(currentStep, stateForValidate, bannerTextGeneration, setSaving, dashboardRoute)
            );
        }
    };
};
